import { Anchor } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import type { MRT_ColumnDef } from "mantine-react-table";
import type { MouseEvent } from "react";
import { useMemo, useState } from "react";
import { useArtifactHandler } from "src/utils/artifactHandler";
import { buildAccessorKey, buildColumnId, getMetadataValue } from "./columnHelpers";

const DEFAULT_FALLBACK_LABEL = "N/A";

type MetadataRowLike = {
    fields?: Record<string, unknown>;
    _source?: {
        metadata?: Record<string, unknown>;
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

type ColumnOverrides<T extends MetadataRowLike> = Partial<MRT_ColumnDef<T>>;

interface BaseLinkColumnOptions<T extends MetadataRowLike> {
    namespace: string;
    field: string;
    header: string;
    fallback?: string;
    overrides?: ColumnOverrides<T>;
    accessor?: (row: T) => unknown;
}

const getDisplayName = (url: string): string => {
    try {
        let filename = "";

        if (url.includes("object_name=")) {
            const match = url.match(/object_name=([^&]+)/);
            if (match?.[1]) {
                const decoded = decodeURIComponent(match[1]);
                const parts = decoded.split("/");
                filename = parts[parts.length - 1] || "";
            }
        } else {
            const urlParts = url.split("/");
            const lastPart = urlParts[urlParts.length - 1];
            if (lastPart?.includes(".")) {
                filename = decodeURIComponent(lastPart);
            }
        }

        if (filename) {
            const lastDotIndex = filename.lastIndexOf(".");
            if (lastDotIndex > 0) {
                return filename.substring(0, lastDotIndex);
            }
            return filename;
        }

        return "View Document";
    } catch {
        return "View Document";
    }
};

const LinkCell = ({ url }: { url: string }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { handleArtifactClick, getFullUrl } = useArtifactHandler();

    const displayName = useMemo(() => getDisplayName(url), [url]);

    if (!url || url === DEFAULT_FALLBACK_LABEL) {
        return <span>{url || DEFAULT_FALLBACK_LABEL}</span>;
    }

    const handleClick = async (e: MouseEvent<HTMLAnchorElement>) => {
        if (isLoading) {
            e.preventDefault();
            return;
        }
        setIsLoading(true);
        try {
            await handleArtifactClick(e, url, [], undefined);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Anchor
            href={getFullUrl(url)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            aria-label={`Open ${displayName} in new window`}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: isLoading ? "wait" : "pointer",
                textDecoration: "underline",
                fontWeight: 500,
            }}
        >
            <span>{isLoading ? "Opening..." : displayName}</span>
            {!isLoading && <IconExternalLink size={14} />}
        </Anchor>
    );
};

export const createLinkColumn = <T extends MetadataRowLike>(
    options: BaseLinkColumnOptions<T>,
): MRT_ColumnDef<T> => {
    const {
        namespace,
        field,
        header,
        fallback = DEFAULT_FALLBACK_LABEL,
        accessor,
        overrides,
    } = options;

    return {
        id: buildColumnId(namespace, field),
        header,
        accessorKey: buildAccessorKey(field),
        accessorFn: (row) => {
            const value = accessor ? accessor(row) : getMetadataValue(row, field);
            if (value === undefined || value === null || value === "") {
                return fallback;
            }
            return value;
        },
        Cell: ({ cell }) => {
            const url = cell.getValue<string>();
            return <LinkCell url={url} />;
        },
        enableClickToCopy: false,
        ...overrides,
    } satisfies MRT_ColumnDef<T>;
};
